import React from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIVITY_CATEGORIES, type ActivityCategory } from '@/types/activity';
import MemberFilterTypeahead, { type MemberFilterOption } from '@/components/shared/MemberFilterTypeahead';
import HelpDot from '@/components/help/HelpDot';
import { ACTIVITY_MEMBER_FILTER_TIP } from '@/data/help-inline-tips';

import './activity-filters-bar.css';

interface ActivityFiltersBarProps {
  category: ActivityCategory;
  userId: string;
  createdAfter: string;
  createdBefore: string;
  memberOptions: MemberFilterOption[];
  onCategoryChange: (category: ActivityCategory) => void;
  onUserIdChange: (userId: string) => void;
  onCreatedAfterChange: (value: string) => void;
  onCreatedBeforeChange: (value: string) => void;
}

const ActivityFiltersBar: React.FC<ActivityFiltersBarProps> = ({
  category,
  userId,
  createdAfter,
  createdBefore,
  memberOptions,
  onCategoryChange,
  onUserIdChange,
  onCreatedAfterChange,
  onCreatedBeforeChange
}) => {
  const { t } = useTranslation('screens');

  return (
    <div className="activity-filter-bar">
      <div className="activity-filter-bar__start">
        <div className="list-seg" role="tablist" aria-label={t('filters.filterByCategory')}>
          {ACTIVITY_CATEGORIES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={category === id}
              className={`list-seg__btn${category === id ? ' list-seg__btn--active' : ''}`}
              onClick={() => onCategoryChange(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="activity-filter-bar__selects" role="group" aria-label={t('filters.memberAndDateFilters')}>
          <div className="activity-filter-bar__member-filter">
            <MemberFilterTypeahead
              id="activity-member-filter"
              value={userId || null}
              members={memberOptions}
              maxResults={5}
              onChange={(nextUserId) => onUserIdChange(nextUserId ?? '')}
            />
            <HelpDot
              title={ACTIVITY_MEMBER_FILTER_TIP.title}
              body={ACTIVITY_MEMBER_FILTER_TIP.body}
              storageKey={ACTIVITY_MEMBER_FILTER_TIP.storageKey}
            />
          </div>

          <label className="activity-filter-bar__date-pill">
            <span className="activity-filter-bar__date-label">{t('filters.from')}</span>
            <input
              type="date"
              className="activity-filter-bar__date-input"
              value={createdAfter}
              aria-label={t('filters.fromDateAria')}
              onChange={(e) => onCreatedAfterChange(e.target.value)}
            />
          </label>

          <label className="activity-filter-bar__date-pill">
            <span className="activity-filter-bar__date-label">{t('filters.to')}</span>
            <input
              type="date"
              className="activity-filter-bar__date-input"
              value={createdBefore}
              aria-label={t('filters.toDateAria')}
              onChange={(e) => onCreatedBeforeChange(e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default ActivityFiltersBar;
